import { useCallback, useEffect, useState } from "react";
import {
  ActionIcon,
  Button,
  Drawer,
  Group,
  Indicator,
  Loader,
  ScrollArea,
  Stack,
  Text,
  ThemeIcon,
  Tooltip,
  UnstyledButton,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  IconBell,
  IconCheck,
  IconInfoCircle,
  IconAlertTriangle,
  IconAlertOctagon,
  IconTrash,
} from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import type { Notification, NotificationSeverity } from "@wwrp/shared";
import { notificationsApi } from "../api/notifications.js";
import { useAuth } from "../contexts/AuthContext.js";

const POLL_MS = 30_000;

const SEVERITY_META: Record<NotificationSeverity, { color: string; Icon: typeof IconInfoCircle }> =
  {
    info: { color: "blue", Icon: IconInfoCircle },
    success: { color: "green", Icon: IconCheck },
    warning: { color: "yellow", Icon: IconAlertTriangle },
    danger: { color: "red", Icon: IconAlertOctagon },
  };

function formatRelative(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const sec = Math.round(diff / 1000);
  if (sec < 60) return "just now";
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 30) return `${day}d ago`;
  return new Date(iso).toLocaleDateString("en-US");
}

export function NotificationsButton() {
  const { user } = useAuth();
  const [opened, { open, close }] = useDisclosure(false);
  const qc = useQueryClient();
  const navigate = useNavigate();

  // Lightweight unread-count poller — runs whenever the user is authed,
  // regardless of drawer state, so the indicator stays current.
  const unreadQuery = useQuery({
    queryKey: ["notifications", "unread-count"],
    queryFn: () => notificationsApi.unreadCount(),
    enabled: Boolean(user),
    refetchInterval: POLL_MS,
    refetchOnWindowFocus: true,
  });

  // Full list — only fetches while the drawer is open.
  const listQuery = useQuery({
    queryKey: ["notifications", "list"],
    queryFn: () => notificationsApi.list({ limit: 50 }),
    enabled: Boolean(user) && opened,
    refetchInterval: opened ? POLL_MS : false,
  });

  const invalidate = useCallback(() => {
    void qc.invalidateQueries({ queryKey: ["notifications"] });
  }, [qc]);

  const markReadMut = useMutation({
    mutationFn: (id: number) => notificationsApi.markRead(id),
    onSuccess: invalidate,
  });

  const markAllReadMut = useMutation({
    mutationFn: () => notificationsApi.markAllRead(),
    onSuccess: invalidate,
  });

  const removeMut = useMutation({
    mutationFn: (id: number) => notificationsApi.remove(id),
    onSuccess: invalidate,
  });

  // Tick every 60s while the drawer is open so formatRelative stays current.
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!opened) return;
    const id = setInterval(() => setTick((t) => t + 1), 60_000);
    return () => clearInterval(id);
  }, [opened]);

  const unread = unreadQuery.data?.unread ?? 0;
  const items = listQuery.data?.items ?? [];

  const handleClickItem = (n: Notification) => {
    if (!n.readAt) markReadMut.mutate(n.id);
    if (n.link) {
      close();
      navigate(n.link);
    }
  };

  const indicatorLabel = unread > 99 ? "99+" : unread > 0 ? String(unread) : "";

  return (
    <div style={{ display: "flex", alignItems: "center" }}>
      <Tooltip label="Notifications">
        <Indicator
          withBorder
          offset={6}
          size={unread > 0 ? 16 : 12}
          color="red"
          processing={unread > 0}
          disabled={unread === 0}
          label={indicatorLabel || undefined}
        >
          <ActionIcon
            variant="transparent"
            c="inherit"
            onClick={open}
            size={42}
            aria-label="Open notifications"
          >
            <IconBell stroke={1.5} />
          </ActionIcon>
        </Indicator>
      </Tooltip>

      <Drawer.Root position="right" opened={opened} onClose={close} size={420}>
        <Drawer.Overlay />
        <Drawer.Content pos="relative">
          <Drawer.Header>
            <Drawer.Title mr="1rem">
              Notifications
              {unread > 0 && (
                <Text component="span" c="dimmed" size="sm" ml="xs">
                  ({unread} unread)
                </Text>
              )}
            </Drawer.Title>
            <Drawer.CloseButton />
          </Drawer.Header>

          <Drawer.Body
            p={0}
            style={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden" }}
          >
            {unread > 0 && (
              <Group justify="flex-end" px="md" py="xs" style={{ flexShrink: 0 }}>
                <Button
                  size="xs"
                  variant="subtle"
                  loading={markAllReadMut.isPending}
                  onClick={() => markAllReadMut.mutate()}
                >
                  Mark all as read
                </Button>
              </Group>
            )}

            <ScrollArea style={{ flex: 1 }}>
              {listQuery.isLoading ? (
                <Group justify="center" mt="xl">
                  <Loader size="sm" />
                </Group>
              ) : items.length === 0 ? (
                <Text c="dimmed" size="sm" ta="center" mt="xl">
                  No notifications yet.
                </Text>
              ) : (
                <Stack gap={0}>
                  {items.map((n) => {
                    const meta = SEVERITY_META[n.severity];
                    const Icon = meta.Icon;
                    return (
                      <Group
                        key={n.id}
                        wrap="nowrap"
                        align="flex-start"
                        gap="sm"
                        px="md"
                        py="sm"
                        style={{
                          borderBottom: "1px solid var(--mantine-color-default-border)",
                          background: n.readAt ? undefined : "var(--mantine-color-default-hover)",
                          cursor: n.link ? "pointer" : "default",
                        }}
                      >
                        <ThemeIcon variant="light" color={meta.color} size="md" radius="xl">
                          <Icon size={16} stroke={1.8} />
                        </ThemeIcon>

                        <UnstyledButton
                          style={{ flex: 1, minWidth: 0, textAlign: "left" }}
                          onClick={() => handleClickItem(n)}
                        >
                          <Text fw={n.readAt ? 400 : 600} size="sm" lineClamp={2}>
                            {n.title}
                          </Text>
                          {n.body && (
                            <Text c="dimmed" size="xs" mt={2} lineClamp={3}>
                              {n.body}
                            </Text>
                          )}
                          <Text c="dimmed" size="xs" mt={4}>
                            {formatRelative(n.createdAt)}
                            {n.createdByName ? ` \u00b7 ${n.createdByName}` : ""}
                          </Text>
                        </UnstyledButton>

                        <Tooltip label="Dismiss">
                          <ActionIcon
                            variant="subtle"
                            color="gray"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeMut.mutate(n.id);
                            }}
                            aria-label="Dismiss notification"
                          >
                            <IconTrash size={14} stroke={1.6} />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    );
                  })}
                </Stack>
              )}
            </ScrollArea>
          </Drawer.Body>
        </Drawer.Content>
      </Drawer.Root>
    </div>
  );
}
